import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import AdminLayout from "@/components/admin/AdminLayout";
import axios from "axios";
import { Plus, Edit2, Trash2, Eye, X, Save, Loader2 } from "lucide-react";
import { ADMIN } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminEmailTemplates() {
  const { authHeader } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [form, setForm] = useState({ template_name: "", subject: "", body: "" });
  const [saving, setSaving] = useState(false);

  const fetchTemplates = () => {
    setLoading(true);
    axios.get(`${API}/templates`, { headers: authHeader() })
      .then(r => setTemplates(r.data.templates || []))
      .finally(() => setLoading(false));
  };
  useEffect(fetchTemplates, []);

  const openCreate = () => { setForm({ template_name: "", subject: "", body: "" }); setEditing(null); setShowModal(true); };
  const openEdit = (t) => { setForm({ template_name: t.template_name, subject: t.subject, body: t.body }); setEditing(t.id); setShowModal(true); };

  const handleSave = async () => {
    if (!form.template_name || !form.subject) return;
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/templates/${editing}`, form, { headers: authHeader() });
      } else {
        await axios.post(`${API}/templates`, form, { headers: authHeader() });
      }
      setShowModal(false);
      fetchTemplates();
    } catch {}
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    await axios.delete(`${API}/templates/${id}`, { headers: authHeader() }).catch(() => {});
    fetchTemplates();
  };

  return (
    <AdminLayout title="Email Templates">
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <p className="text-sm text-slate-500">{templates.length} template{templates.length !== 1 ? "s" : ""}</p>
          <button onClick={openCreate}
            className="flex items-center gap-2 bg-blue-600 text-white px-5 py-2.5 rounded-full text-sm font-medium hover:bg-blue-700 transition-all">
            <Plus size={16} /> New Template
          </button>
        </div>

        <div className="grid gap-4">
          {loading ? (
            <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-400">Loading...</div>
          ) : templates.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-400">No templates yet.</div>
          ) : templates.map(t => (
            <div key={t.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-slate-900 text-sm">{t.template_name}</h3>
                  <p className="text-sm text-slate-500 mt-0.5">Subject: {t.subject}</p>
                  <p className="text-xs text-slate-400 mt-1 line-clamp-2">{t.body?.substring(0, 100)}...</p>
                </div>
                <div className="flex gap-1.5 ml-4">
                  <button onClick={() => setPreviewTemplate(t)} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                    <Eye size={14} />
                  </button>
                  <button onClick={() => openEdit(t)} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                    <Edit2 size={14} />
                  </button>
                  <button onClick={() => handleDelete(t.id)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Edit/Create Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100">
              <h3 className="font-semibold text-slate-900">{editing ? "Edit Template" : "New Template"}</h3>
              <button onClick={() => setShowModal(false)} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-50">
                <X size={18} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Template Name *</label>
                <input value={form.template_name} onChange={e => setForm({ ...form, template_name: e.target.value })}
                  placeholder="e.g., Clinic Confirmation"
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Subject Line *</label>
                <input value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })}
                  placeholder="e.g., AvA received your Apexum intake"
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Body</label>
                <p className="text-xs text-slate-400 mb-1.5">Use {"{{first_name}}"}, {"{{selected_pathways}}"} as variables</p>
                <textarea value={form.body} onChange={e => setForm({ ...form, body: e.target.value })}
                  rows={8}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl resize-y focus:outline-none focus:ring-2 focus:ring-blue-500/20 font-mono" />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-slate-100 flex gap-3">
              <button onClick={() => setShowModal(false)} className="flex-1 py-2.5 text-sm border border-slate-200 rounded-full text-slate-600 hover:bg-slate-50">Cancel</button>
              <button onClick={handleSave} disabled={saving || !form.template_name}
                className="flex-1 flex items-center justify-center gap-2 bg-blue-600 text-white py-2.5 rounded-full text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-all">
                {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {previewTemplate && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg">
            <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100">
              <h3 className="font-semibold text-slate-900">Preview: {previewTemplate.template_name}</h3>
              <button onClick={() => setPreviewTemplate(null)} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-50">
                <X size={18} />
              </button>
            </div>
            <div className="p-6">
              <div className="bg-slate-50 rounded-xl p-4 mb-4">
                <p className="text-xs text-slate-500 mb-1">Subject</p>
                <p className="text-sm font-medium text-slate-900">{previewTemplate.subject}</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="text-xs text-slate-500 mb-2">Body</p>
                <pre className="text-sm text-slate-700 whitespace-pre-wrap font-sans">{previewTemplate.body}</pre>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
